import { Field, FieldProps, FormikProps } from 'formik';
import * as React from 'react';
import { OperationVariables, QueryResult } from '@apollo/client';
import {
  FieldRow,
  StyledFormGroup,
  StyledFormSelectFormik,
  WrappedQuery,
  StyledFormikDatePicker,
} from '../../../../components/shared';
import {
  FILTERED_PERSONNEL_POSITIONS,
  ALL_STAKEHOLDER_PHASES,
  FILTERED_SUBJECTS,
  FILTERED_REASON_FOR_TERMINATIONS,
} from '../../../../graphql';
import { formatSelectValue } from '../../../../utils';
import { Option, PeopleProfileInstitutionFormType } from '../../../../utils/types';
import { InstitutionsCard } from './institutionsCard';
import { PersonDetailsFormType } from '../forms/PersonDetailsForm';

type FormType = PersonDetailsFormType & {
  typeId: Option | undefined;
  portal: string;

  dateExited: Date | null;
  designation: string;
  districtId: Option | undefined;
  emailAddress: string;
  emisNumber: number | undefined;
  persalNumber: string;
  personnelPositionId: Option | undefined;
  personnelPositionOther: string;
  phaseId: Option | undefined;
  subjectIds: Option[];
  terminationId: Option | undefined;

  primaryInstitutionId: Option | undefined;
  institutionIds: Option[];
  associatedInstitutions: PeopleProfileInstitutionFormType[];
};

type Props = {
  formikProps: FormikProps<FormType>;
  userRoles?: any;
};

const FieldsForDistrictOfficial: React.FC<Props> = props => {
  const { formikProps, userRoles } = props;
  const {
    values: { dateExited },
  } = formikProps;

  const canLinkInstitutions = ['INSTITUTION_ADMIN', 'INSTITUTION_MEMBER'].some(
    (item: any) => userRoles && userRoles.includes(item)
  );
  return (
    <React.Fragment>
      <FieldRow>
        <Field
          name="emailAddress"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Email Address" mr={4} />
          )}
        />
        <WrappedQuery
          query={FILTERED_PERSONNEL_POSITIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="personnelPositionId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Personnel Position"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredPersonnelPositions &&
                          data.filteredPersonnelPositions.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        {formikProps.values &&
          formikProps.values.personnelPositionId &&
          formikProps.values.personnelPositionId.label === 'Other' && (
            <Field
              name="personnelPositionOther"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup {...field} field={field} form={formikProps} label="Personnel Position Other" mr={4} />
              )}
            />
          )}
        <Field
          name="designation"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Designation" />
          )}
        />
      </FieldRow>
      <FieldRow>
        <WrappedQuery query={ALL_STAKEHOLDER_PHASES} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="phaseId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Phase"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.stakeholderPhases.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        <WrappedQuery
          query={FILTERED_SUBJECTS}
          variables={{
            input: {
              phaseId: formikProps.values.phaseId && formikProps.values.phaseId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="subjectIds"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Subject"
                    form={formikProps}
                    isMulti={true}
                    options={
                      loading || error
                        ? []
                        : data.filteredSubjects &&
                          data.filteredSubjects.map((item: any) => {
                            return formatSelectValue({
                              label: item.subjectGradeReference,
                              value: item.id,
                            });
                          })
                    }
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        <Field
          render={() => (
            <StyledFormikDatePicker
              isSingleDate={true}
              form={formikProps}
              label="Date Exited / Termination"
              dateLabel="dateExited"
              date={dateExited || null}
              mr={4}
            />
          )}
        />
        <WrappedQuery
          query={FILTERED_REASON_FOR_TERMINATIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="terminationId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Reason for Exit"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredReasonForTerminations &&
                          data.filteredReasonForTerminations.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={2}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        <FieldRow flex={0.5}>
          <Field
            name="persalNumber"
            render={({ field }: FieldProps<FormType>) => (
              <StyledFormGroup {...field} field={field} form={formikProps} label="Persal Number" />
            )}
          />
        </FieldRow>
      </FieldRow>
      <InstitutionsCard
        formikProps={formikProps}
        viewMode={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
        currentInstitutionsOnly={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
      />
    </React.Fragment>
  );
};

export default FieldsForDistrictOfficial;
